/* ---------------------------------------------------------------------------
   Actual against planned — FIDIC Sub-Clause 4.21(h).

   Pure, and taking the three lists rather than the database, so the record's
   card, the report and any later screen all reach the same conclusion and it
   can be checked without one. Same shape `summariseProgress` and
   `safetySummary` have.

   ══ THE THREE INPUTS, AND WHY NONE OF THEM IS OPTIONAL ══

   A BASELINE — planned dates, frozen at a moment somebody chose. Without it
   there is no plan and the honest answer to "are we late" is that the register
   cannot say.

   MATCHES — which component's entries realise which scheduled activity, each
   confirmed by a person. Without them the two vocabularies are two lists that
   happen to contain similar words.

   ENTRIES — what was recorded. Only ACCEPTED ones count toward a variance
   reported to a client, and that is enforced by the caller passing the set it
   means: see `varianceFor`, which counts whatever it is given and says so.

   ══ THE TWO THINGS THIS DELIBERATELY WILL NOT DO ══

   **It will not infer that work has finished.** The register knows when the
   last entry against a component was recorded. It does not know that no more
   are coming. Treating the latest entry as a finish would report every
   activity complete the moment a site paused for a week, and would restate
   last month's variance every time a new entry arrived. So a finish variance
   exists only where somebody declared a completion, and everything else is
   reported as in progress with its latest entry shown.

   **It will not give one number for percent complete.** It gives two, and the
   truth is between them: what has been DECLARED complete, and what has been
   STARTED OR COMPLETED. A single figure would have to guess how far through an
   in-progress activity the site is, and the only honest guesses are 0% and
   100% — which is exactly the pair this returns.

   ══ AND THE WEIGHTING, WHICH IS THE PART TO ARGUE WITH ══

   Percent complete here is weighted BY PLANNED DURATION, not by value. A
   value-weighted curve is what a client and a payment certificate actually
   want, and it needs planned quantities against each activity — which the
   modeller can compute (`boq/QuantityExtractor.ts`) and which are NOT in the
   model state this register reads: they are derived from the live Three.js
   scene and never persisted. So they are not available here, and inventing a
   value weighting from durations would produce a curve that looked like money
   and measured time.

   Duration weighting is a real and standard method — it is what a schedule
   that is not cost-loaded gives you — and every figure this module produces
   says which it is. When a quantity export exists, `weightOf` is the one
   function that changes.
   --------------------------------------------------------------------------- */

import { daysBetween } from './periods.ts';
import { sameComponent } from './componentTree.ts';
import { entryFitsKind } from './activityKinds.ts';
import type { LinkType } from './cpm.ts';
import type { WorkCalendar } from './calendar.ts';
import type { ContractHeader } from './contract.ts';
import type { WorksKind } from './phase.ts';

/** One planned activity, as a frozen baseline holds it. */
export interface BaselineActivity {
  activityId: string;
  name: string;
  kind: string;
  parentId: string | null;
  plannedStart: string;
  plannedFinish: string;
  floatDays: number;
  isGroup: boolean;
  /**
   * Planned duration in WORKING days, on the baseline's calendar (LC-43).
   * Absent on a baseline frozen before durations were stored, where the
   * forecast counts it from the two planned dates.
   */
  durationDays?: number;
  /** Start no earlier than, where the modeller pinned one. */
  notBefore?: string | null;
  /**
   * The component it builds, where the programme knows: one derived from an
   * imported model's parts (src/data/importedProgramme.ts) does, by the
   * export's own id. Absent on a programme from the modeller's schedule.
   */
  component?: string | null;
  componentId?: string | null;
}

/** One stored link: the logic the baseline was solved with (LC-44). */
export interface BaselineLink {
  from: string;
  to: string;
  type: LinkType;
  /** Working days; negative is a lead. */
  lagDays: number;
}

/** Proposed by a planner, accepted by an approver who is not them (LC-40). */
export type BaselineState = 'proposed' | 'accepted';

/** A baseline's header. */
export interface Baseline {
  id: string;
  bridgeId: string;
  name: string;
  projectStart: string;
  modelId: string | null;
  takenBy: string | null;
  takenAt: string;
  note: string;
  activities: BaselineActivity[];
  /**
   * LC-40. Absent reads as accepted: every baseline frozen before states
   * existed was measured against, and migration 0041 says so.
   */
  state?: BaselineState;
  /** Why the programme was revised. Required on every baseline after the first. */
  reason?: string | null;
  acceptedBy?: string | null;
  acceptedAt?: string | null;
  /** The working calendar its durations count on (LC-43), resolved by the server. */
  calendarId?: string | null;
  calendar?: WorkCalendar | null;
  /** The logic it was solved with (LC-44). Empty on a baseline frozen before links were kept. */
  links?: BaselineLink[];
  /** The contract it is administered against (LC-42). */
  contract?: ContractHeader | null;
  /**
   * The works it programmes on a bridge in service (LC-81), or null for a new
   * structure's. Accepting the first of them opens the works.
   */
  worksKind?: WorksKind | null;
}

/** A baseline's header without its activities, as the history lists it. */
export type BaselineHeader = Omit<Baseline, 'activities' | 'links' | 'calendar'>;

/** The header of a baseline in hand, as the history would list it. */
export function headerOf(b: Baseline): BaselineHeader {
  const { activities, links, calendar, ...header } = b;
  return header;
}

/** One confirmed join between the programme and the record. */
export interface ScheduleMatch {
  id: string;
  bridgeId: string;
  activityId: string;
  activityName: string;
  component: string;
  /** The model's id for that component, where the match was made with one. LC-14. */
  componentId?: string | null;
  completedOn: string | null;
  matchedBy: string | null;
  matchedAt: string;
}

/** Where a planned activity has got to. */
export type ActivityState = 'unmatched' | 'not-started' | 'in-progress' | 'complete';

export interface VarianceRow {
  activityId: string;
  name: string;
  kind: string;
  plannedStart: string;
  plannedFinish: string;
  floatDays: number;
  /** How many days this activity was planned to take. The weight. */
  plannedDays: number;
  state: ActivityState;
  /** The component its entries are matched to, or null where nobody has matched it. */
  component: string | null;
  /** And the model's id for it, where the match carries one. */
  componentId: string | null;
  /** The earliest entry against that component, or null. */
  firstEntry: string | null;
  /** The latest. NOT a finish — see the header. */
  latestEntry: string | null;
  /** How many entries are behind those two dates. */
  entries: number;
  /** Declared finish, or null. Only this produces a finish variance. */
  completedOn: string | null;
  /**
   * Days between the planned start and the first entry. Positive is late.
   * Null where nothing has been recorded against a matched component.
   */
  startVarianceDays: number | null;
  /** Days between the planned finish and the declared finish. Positive is late. */
  finishVarianceDays: number | null;
}

/** The minimum of a progress entry this module needs. */
export interface CountableEntry {
  component: string | null;
  componentId?: string | null;
  occurredOn: string;
  /**
   * The activity-library id. Where given, only entries of the kind of work the
   * planned activity is start it (LC-41) — see src/data/activityKinds.ts.
   */
  activity?: string | null;
}

/* --------------------------- as at a date --------------------------- */

/**
 * The entries a variance counts AS AT a day (LC-51): accepted, on the register,
 * done on or before it, and accepted on or before it. An entry accepted since
 * was a draft on that day, so a report for August printed in October counts
 * what August's did. The register keeps no history of an entry's state, only
 * when it was accepted — which is enough, because accepted is the one state a
 * variance counts. An accepted entry with no review date (rows written before
 * one was stored) is counted from the day the work was done.
 */
export function entriesAsOf<
  T extends { state: string; pending?: boolean; occurredOn: string; reviewedAt: string | null },
>(entries: readonly T[], asOf: string): T[] {
  return entries.filter(
    (e) =>
      e.state === 'accepted' &&
      !e.pending &&
      e.occurredOn <= asOf &&
      (e.reviewedAt === null || e.reviewedAt.slice(0, 10) <= asOf),
  );
}

/**
 * The matches as at a day (LC-51): a finish declared for a later day had not
 * been declared yet, so the activity reads as in progress. The match itself is
 * kept — it is an opinion about which work is which, not a dated event, and it
 * is replaced in place, so the day it was first formed is not stored.
 */
export function matchesAsOf(matches: readonly ScheduleMatch[], asOf: string): ScheduleMatch[] {
  return matches.map((m) => (m.completedOn !== null && m.completedOn > asOf ? { ...m, completedOn: null } : m));
}

/**
 * How much of the programme one activity is.
 *
 * Planned duration in days, inclusive at both ends, and never less than one —
 * a milestone planned to start and finish on the same day is a day of the
 * programme rather than nothing at all. Groups weigh nothing: they roll their
 * children up and counting both would count the work twice.
 *
 * THE ONE FUNCTION TO CHANGE when planned quantities become available. See the
 * header.
 */
export function weightOf(a: BaselineActivity): number {
  if (a.isGroup) return 0;
  return Math.max(1, daysBetween(a.plannedStart, a.plannedFinish) + 1);
}

/**
 * Signed days between two calendar dates. Positive where `actual` is later.
 *
 * `daysBetween` is unsigned and clamps at zero, which is right for the length
 * of a period and wrong for a variance: an activity that started four days
 * EARLY than planned is as much a fact as one four days late, and reporting
 * both as zero would make an early programme indistinguishable from an
 * on-time one.
 */
export function signedDays(planned: string, actual: string): number {
  return actual >= planned ? daysBetween(planned, actual) : -daysBetween(actual, planned);
}

/**
 * The whole comparison, one row per planned activity.
 *
 * WBS groups are kept in the output and marked with `plannedDays` of zero, so
 * a report can indent under them without them polluting a count.
 */
export function varianceFor(
  baseline: Baseline,
  matches: readonly ScheduleMatch[],
  entries: readonly CountableEntry[],
): VarianceRow[] {
  const byActivity = new Map(matches.map((m) => [m.activityId, m]));

  /* Entries grouped by the component they name, once, rather than filtered per
     activity — a 300-activity baseline against a few thousand entries would
     otherwise be a scan per row. Grouped by label; `sameComponent` then drops
     the ones whose id says they are a different part of the same name (LC-14). */
  const byComponent = new Map<string, CountableEntry[]>();
  const byId = new Map<string, CountableEntry[]>();
  const add = (into: Map<string, CountableEntry[]>, key: string, e: CountableEntry): void => {
    const list = into.get(key);
    if (list) list.push(e);
    else into.set(key, [e]);
  };
  for (const e of entries) {
    if (e.componentId) add(byId, e.componentId, e);
    if (e.component === null || e.component === '') continue;
    add(byComponent, e.component, e);
  }

  const entriesOf = (m: ScheduleMatch): CountableEntry[] => {
    const ref = { component: m.component, componentId: m.componentId ?? null };
    const named = new Set(byComponent.get(m.component) ?? []);
    if (ref.componentId) for (const e of byId.get(ref.componentId) ?? []) named.add(e);
    return [...named].filter((e) => sameComponent(ref, e));
  };

  return baseline.activities.map((a) => {
    const match = byActivity.get(a.activityId) ?? null;
    /* Of the kind of work this activity is, too: a setting-out entry against a
       pile cap does not start the pour (LC-41). */
    const mine = match ? entriesOf(match).filter((e) => entryFitsKind(a.kind, e.activity)) : [];

    const dates = mine.map((e) => e.occurredOn).sort();
    const firstEntry = dates[0] ?? null;
    const latestEntry = dates[dates.length - 1] ?? null;
    const completedOn = match?.completedOn ?? null;

    let state: ActivityState;
    if (!match) state = 'unmatched';
    else if (completedOn !== null) state = 'complete';
    else if (dates.length > 0) state = 'in-progress';
    else state = 'not-started';

    return {
      activityId: a.activityId,
      name: a.name,
      kind: a.kind,
      plannedStart: a.plannedStart,
      plannedFinish: a.plannedFinish,
      floatDays: a.floatDays,
      plannedDays: weightOf(a),
      state,
      component: match?.component ?? null,
      componentId: match?.componentId ?? null,
      firstEntry,
      latestEntry,
      entries: mine.length,
      completedOn,
      startVarianceDays: firstEntry === null ? null : signedDays(a.plannedStart, firstEntry),
      /* ONLY WHERE A COMPLETION WAS DECLARED. See the header: the register does
         not know that no more entries are coming. */
      finishVarianceDays:
        completedOn === null ? null : signedDays(a.plannedFinish, completedOn),
    };
  });
}

/* --------------------------- how far through --------------------------- */

export interface Completion {
  /** Total planned days in the baseline, excluding groups. */
  plannedDays: number;
  /** Planned days on activities with a declared completion. */
  completeDays: number;
  /** Planned days on activities with entries but no declared completion. */
  startedDays: number;
  /**
   * The lower bound: what has been declared finished, as a percentage.
   *
   * 0–100, and 0 where the baseline is empty rather than NaN — a division that
   * produced NaN would print as "NaN%" on a report going to a client.
   */
  declaredPercent: number;
  /** The upper bound: declared plus started. The truth is between the two. */
  startedPercent: number;
  /** How many activities are matched, of how many are work. */
  matched: { of: number; count: number };
}

/**
 * Two bounds and never one number. See the header for why.
 */
export function completionOf(rows: readonly VarianceRow[]): Completion {
  let plannedDays = 0;
  let completeDays = 0;
  let startedDays = 0;
  let work = 0;
  let matched = 0;

  for (const r of rows) {
    if (r.plannedDays === 0) continue;
    plannedDays += r.plannedDays;
    work += 1;
    if (r.state !== 'unmatched') matched += 1;
    if (r.state === 'complete') completeDays += r.plannedDays;
    else if (r.state === 'in-progress') startedDays += r.plannedDays;
  }

  const pct = (n: number): number =>
    plannedDays === 0 ? 0 : Math.round((n / plannedDays) * 1000) / 10;

  return {
    plannedDays,
    completeDays,
    startedDays,
    declaredPercent: pct(completeDays),
    startedPercent: pct(completeDays + startedDays),
    matched: { of: work, count: matched },
  };
}

/* --------------------------- the curve --------------------------- */

export interface CurvePoint {
  /** `YYYY-MM`. Monthly, because a construction programme is read by month. */
  month: string;
  /** Cumulative planned percentage complete by the end of this month. */
  planned: number;
  /**
   * Cumulative declared-complete percentage by the end of this month, or null
   * for a month that has not happened yet.
   *
   * NULL AND NOT ZERO for the future. A curve that ran the actual line along
   * the floor to the end of the programme would read as a project that has
   * stopped, when what it means is a project that has not got there.
   */
  actual: number | null;
}

/**
 * Planned against actual, cumulative, by month — the S-curve.
 *
 * AN ACTIVITY COUNTS ON ITS FINISH, not spread across its duration. Spreading
 * is what a cost-loaded curve does and it needs a weighting this does not have
 * (see the header); counting on the finish is what a milestone-weighted curve
 * does, and it is honest about being one. The visible difference is that this
 * curve is a staircase rather than a smooth S, which is the correct
 * appearance for a programme measured in completed activities.
 *
 * `today` is passed rather than read, for the reason src/data/periods.ts takes
 * it: a curve whose shape depended on the clock could not be checked.
 */
export function progressCurve(rows: readonly VarianceRow[], today: string): CurvePoint[] {
  const total = rows.reduce((sum, r) => sum + r.plannedDays, 0);
  if (total === 0) return [];

  const plannedBy = new Map<string, number>();
  const actualBy = new Map<string, number>();

  for (const r of rows) {
    if (r.plannedDays === 0) continue;
    const pm = r.plannedFinish.slice(0, 7);
    plannedBy.set(pm, (plannedBy.get(pm) ?? 0) + r.plannedDays);
    if (r.completedOn !== null) {
      const am = r.completedOn.slice(0, 7);
      actualBy.set(am, (actualBy.get(am) ?? 0) + r.plannedDays);
    }
  }

  const months = [...new Set([...plannedBy.keys(), ...actualBy.keys()])].sort();
  if (months.length === 0) return [];

  /* Every month between the first and the last, so the curve has no gaps where
     nothing was planned to finish — a chart that skipped an empty month would
     compress time and make a flat stretch look like progress. */
  const filled: string[] = [];
  let cursor = months[0]!;
  const last = months[months.length - 1]!;
  while (cursor <= last && filled.length < 600) {
    filled.push(cursor);
    const year = Number(cursor.slice(0, 4));
    const month = Number(cursor.slice(5, 7));
    cursor =
      month === 12
        ? `${year + 1}-01`
        : `${year}-${String(month + 1).padStart(2, '0')}`;
  }

  const nowMonth = today.slice(0, 7);
  let planned = 0;
  let actual = 0;

  return filled.map((month) => {
    planned += plannedBy.get(month) ?? 0;
    actual += actualBy.get(month) ?? 0;
    return {
      month,
      planned: Math.round((planned / total) * 1000) / 10,
      actual: month > nowMonth ? null : Math.round((actual / total) * 1000) / 10,
    };
  });
}

/* --------------------------- what is late --------------------------- */

/**
 * The activities worth talking about, worst first.
 *
 * ORDERED BY FLOAT AND THEN BY LATENESS, which is the order a programme is
 * read in and not the order a spreadsheet sorts in. An activity eleven days
 * late with forty days of float is not the problem; one three days late on the
 * critical path is. Sorting by lateness alone would put the first at the top
 * of a report every month.
 *
 * `asOf` is what makes a not-started activity late: one whose planned start
 * has passed with nothing recorded is the most important row on this list and
 * has no variance of its own to sort by, because nothing has happened.
 */
export function lateRows(rows: readonly VarianceRow[], asOf: string): VarianceRow[] {
  const late = rows.filter((r) => {
    if (r.plannedDays === 0 || r.state === 'unmatched') return false;
    if (r.state === 'complete') return (r.finishVarianceDays ?? 0) > 0;
    if (r.state === 'not-started') return r.plannedStart < asOf;
    return (r.startVarianceDays ?? 0) > 0 || r.plannedFinish < asOf;
  });

  return late.sort((a, b) => {
    if (a.floatDays !== b.floatDays) return a.floatDays - b.floatDays;
    return latenessOf(b, asOf) - latenessOf(a, asOf);
  });
}

/** When a works entry fell against the programme. */
export type EntryTiming = 'ahead' | 'ontime' | 'late';

/**
 * Whether one entry was done before, inside or after the planned dates of the
 * activities it counts toward — the same matching `varianceFor` does, by
 * component and kind of work. Null where it counts toward none.
 */
export function entryTiming(
  baseline: Baseline,
  matches: readonly ScheduleMatch[],
  e: CountableEntry,
): EntryTiming | null {
  const byActivity = new Map(matches.map((m) => [m.activityId, m]));
  let earliest: string | null = null;
  let found = false;
  for (const a of baseline.activities) {
    if (a.isGroup || !a.plannedStart || !a.plannedFinish) continue;
    const m = byActivity.get(a.activityId);
    if (!m || !sameComponent({ component: m.component, componentId: m.componentId ?? null }, e)) continue;
    if (!entryFitsKind(a.kind, e.activity)) continue;
    if (e.occurredOn >= a.plannedStart && e.occurredOn <= a.plannedFinish) return 'ontime';
    found = true;
    if (earliest === null || a.plannedStart < earliest) earliest = a.plannedStart;
  }
  if (!found) return null;
  return e.occurredOn < earliest! ? 'ahead' : 'late';
}

/** How late a row is today, in days. Zero where it is not late. */
export function latenessOf(r: VarianceRow, asOf: string): number {
  if (r.state === 'complete') return Math.max(0, r.finishVarianceDays ?? 0);
  if (r.state === 'not-started') return Math.max(0, signedDays(r.plannedStart, asOf));
  return Math.max(0, signedDays(r.plannedFinish, asOf));
}
